import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, tap, finalize, shareReplay } from "rxjs/operators";
import { HttpServiceBase } from "../classes/HttpServiceBase";

/**
 * How long a projects payload may be reused.
 *
 * An hour, and not longer, because the payload is perishable in a way its own
 * content does not show: `imageUrl` is a presigned S3 url that the backend signs
 * against the top of the hour and expires two hours later. Reuse it past that
 * and every project photo 403s. An hour is the window in which the signature is
 * certain to still be good.
 */
const PROJECTS_TTL_MS = 60 * 60 * 1000;

/** Versioned, so a change to what is stored retires old entries instead of
 *  handing a new reader a shape it does not expect. */
const PROJECTS_CACHE_KEY = 'projects_cache_v1';

interface ProjectsCacheEntry {
    /** Epoch ms the payload was fetched, which is what the TTL is measured from. */
    at: number;
    data: any;
}

@Injectable({
    providedIn: 'root'
})
export class BackendService extends HttpServiceBase {

    /** Latest payload per language, mirrored from localStorage on first read. */
    private projectsMemory = new Map<string, ProjectsCacheEntry>();

    /** Requests that have gone out and not yet come back, per language. */
    private projectsInFlight = new Map<string, Observable<any>>();

    constructor(
        private http: HttpClient,
    ) {
        super();
        this.assign(this);
    }


    public getInstallation(): Observable<any> {
        console.log('Getting installation with url:', this.baseUrl + '/installation');
        return this._http.post(this.baseUrl + '/installation', { type: "web", identifier: window?.location?.hostname, systemInfo: '' })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public createOrder(data: any): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/create-order', { orderDetails: data })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public getSpaceTypes(): Observable<any> {
        return this._http.get(this.baseUrl + '/space-types')
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public getAutomations(): Observable<any> {
        return this._http.get(this.baseUrl + '/automations')
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public getCountryFromIP(): Observable<any> {
        return this._http.post(this.baseUrl + '/get-country-from-ip', {})
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public getCountryCodes(): Observable<any> {
        return this._http.post(this.baseUrl + '/phone-codes', {})
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    /**
     * The public project list, cached per language for an hour.
     *
     * Cached because six screens ask for it — the home page, a project page and
     * the three solution pages — and each asked again on every mount, including
     * a browser Back, which left the carousel empty for the length of the round
     * trip before it popped in.
     *
     * Keyed by language rather than cleared on a language switch: the response
     * is localised, the switch does not reload the page, and keying means
     * switching back inside the hour is a hit rather than a refetch. The key is
     * the cookie the interceptor sends as `lang`, so what is stored is filed
     * under the language it was actually requested in.
     */
    public getProjects(): Observable<any> {
        const lang = this.projectsLang();

        const cached = this.readProjectsCache(lang);
        if (cached) {
            return of(cached);
        }

        // A second caller arriving before the first response rides the same
        // request rather than starting another.
        const inFlight = this.projectsInFlight.get(lang);
        if (inFlight) {
            return inFlight;
        }

        const request = this._http.get(this.baseUrl + '/projects')
            .pipe(
                map((response: any) => response),
                tap((response: any) => this.writeProjectsCache(lang, response)),
                finalize(() => this.projectsInFlight.delete(lang)),
                shareReplay({ bufferSize: 1, refCount: false }),
                catchError(this.handleError.bind(this)));

        this.projectsInFlight.set(lang, request);
        return request;
    }

    /** Drops every cached language. Call after anything that edits a project,
     *  so an admin sees their own change without waiting out the hour. */
    public invalidateProjects(): void {
        this.projectsMemory.clear();
        this.projectsInFlight.clear();
        this.writeStoredProjects({});
    }

    /** The same value the interceptor puts on the request as `lang`, so the
     *  cache is filed under the language the payload was actually fetched in. */
    private projectsLang(): string {
        const match = typeof document !== 'undefined'
            ? document.cookie.match(/(?:^|;\s*)preferred_lang=([^;]*)/)
            : null;
        return match ? decodeURIComponent(match[1]) : 'en';
    }

    private readProjectsCache(lang: string): any | null {
        const entry = this.projectsMemory.get(lang) ?? this.readStoredProjects()[lang];
        if (!entry || Date.now() - entry.at > PROJECTS_TTL_MS) {
            return null;
        }
        // Promote a stored hit into memory so a reload pays the parse once.
        this.projectsMemory.set(lang, entry);
        return entry.data;
    }

    private writeProjectsCache(lang: string, data: any): void {
        const entry: ProjectsCacheEntry = { at: Date.now(), data };
        this.projectsMemory.set(lang, entry);
        const stored = this.readStoredProjects();
        stored[lang] = entry;
        this.writeStoredProjects(stored);
    }

    /**
     * localStorage so a full reload inside the hour is served too, not just a
     * route change. Every access is guarded: it throws outright in some private
     * browsing modes, and a quota failure on write must cost the persistence,
     * not the response — the in-memory copy carries on either way.
     */
    private readStoredProjects(): Record<string, ProjectsCacheEntry> {
        try {
            return JSON.parse(localStorage.getItem(PROJECTS_CACHE_KEY) || '{}');
        } catch {
            return {};
        }
    }

    private writeStoredProjects(value: Record<string, ProjectsCacheEntry>): void {
        try {
            localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(value));
        } catch {
            /* nothing to do: the memory cache still answers for this tab */
        }
    }

    public getOrders(latest: boolean): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/get-orders', { latest: latest })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public filterOrders(filters: any): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/filter-orders', { filters: filters })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public getOrderById(id: string): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/get-order-by-id', { orderId: id })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public takeOrder(orderId: string): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/assign-order', { orderId: orderId })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    /** The statuses an order can be set to (Wallio_OrderStatuses). */
    public getOrderStatuses(): Observable<any> {
        return this._http.get(this.baseUrl + '/orders/order-statuses')
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public updateOrderStatus(orderId: string, statusId: string): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/update-order-status', { orderId: orderId, statusId: statusId })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public addComment(orderId: string, comment: string): Observable<any> {
        return this._http.post(this.baseUrl + '/orders/add-comment', { orderId: orderId, comment: comment })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public calculateDashboardStats(): Observable<any> {
        return this._http.get(this.baseUrl + '/orders/calculate-dashboard-stats')
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }

    public calculatePrice(size: number, spaceType: object) {
        return this._http.post(this.baseUrl + '/orders/price-calculation', { size, spaceType })
            .pipe(map((response: any) => response),
                catchError(this.handleError.bind(this)));
    }
}
